import { findMissingTranslationReferences } from '@finance/contracts';
import { catalogHasKey, SUPPORTED_LOCALES } from './catalog-registry';
import { browserLogger } from '../runtime/logging/browser-logger';

export interface ResolvedDescriptorI18nContext {
  kind: string;
  id?: string;
}

function hasCompiledKey(key: string): boolean {
  return catalogHasKey(SUPPORTED_LOCALES.en.catalog, key)
    && catalogHasKey(SUPPORTED_LOCALES.ne.catalog, key);
}

/**
 * Validate an API-resolved presentation descriptor against the exact catalogs
 * loaded by the browser. Localization remains presentation-only: production
 * logs the defect and lets fail-safe rendering continue, while dev/test throws
 * so regressions cannot be normalized into shipped UI.
 */
export function validateResolvedDescriptorI18n<T>(
  value: T,
  context: ResolvedDescriptorI18nContext,
): T {
  const missing = findMissingTranslationReferences(value, hasCompiledKey, context.kind);
  if (missing.length === 0) return value;

  const details = missing.map((reference) => ({
    path: reference.path,
    key: reference.key,
    property: reference.property,
  }));
  browserLogger.error(
    'i18n.resolved_descriptor_missing_key',
    'Resolved experience descriptor references missing translations',
    { ...context, missing: details },
  );

  if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
    throw new Error(
      `Resolved ${context.kind}${context.id ? ` ${context.id}` : ''} has missing translations:\n${missing
        .map((reference) => `${reference.path}: ${reference.key}`)
        .join('\n')}`,
    );
  }

  return value;
}
